import axios, { AxiosResponse } from "axios";

import { getApiGatewayUrl } from "./configService";

class RouteService {
    static mainBackendVersion: keyof Omit<API.ConfigRoutes["main"], "jwKs"> = "v3";
    static productBackendVersion: keyof API.ConfigRoutes["product"] = "v1";
    static storageBackendVersion: keyof API.ConfigRoutes["storage"] = "v1";
    static eventBackendVersion: keyof API.ConfigRoutes["event"] = "v1";
    static streamBackendVersion: keyof API.ConfigRoutes["stream"] = "v2";

    private static routes: API.ConfigRoutes | null = null;

    private static async getRoutes(): Promise<API.ConfigRoutes> {
        if (this.routes) {
            return this.routes;
        }
        const response = await axios.get<object, AxiosResponse<API.ConfigRoutes>>(getApiGatewayUrl() + "/v1/configs/routes");
        this.routes = response.data;
        return this.routes;
    }

    static async getAnnouncementsRoute() {
        const routes = await this.getRoutes();
        return routes.main["v3"].announcements;
    }
    static async getAccountsRoute() {
        const routes = await this.getRoutes();
        return routes.main[RouteService.mainBackendVersion].accounts;
    }
    static async getAdminAccountsRoute() {
        const routes = await this.getRoutes();
        // admin endpoints are not exposed in the gateway routes config, so derive them from the regular route
        return routes.main[RouteService.mainBackendVersion].accounts.replace("/main/api/", "/main/admin/");
    }
    static async getApiKeysRoute() {
        const routes = await this.getRoutes();
        return routes.main[RouteService.mainBackendVersion].apikeys;
    }
    static async getConfigRoute() {
        const routes = await this.getRoutes();
        return routes.main[RouteService.mainBackendVersion].settings;
    }
    static async getAdminClientsRoute() {
        const routes = await this.getRoutes();
        // admin endpoints are not exposed in the gateway routes config, so derive them from the regular route
        return routes.main["v3"].clients.replace("/main/api/", "/main/admin/");
    }
    static async getDevicesRoute() {
        const routes = await this.getRoutes();
        return routes.main[RouteService.mainBackendVersion].devices;
    }
    static async getEventsRoute() {
        const routes = await this.getRoutes();
        return routes.event[RouteService.eventBackendVersion];
    }
    static async getAdminEventsRoute() {
        const routes = await this.getRoutes();
        // admin endpoints are not exposed in the gateway routes config, so derive them from the regular route
        return routes.event[RouteService.eventBackendVersion].replace("/event/api/", "/event/admin/");
    }
    static async getGatewaysRoute() {
        const routes = await this.getRoutes();
        return routes.main[RouteService.mainBackendVersion].gateways;
    }
    static async getGroupsRoute() {
        const routes = await this.getRoutes();
        return routes.main[RouteService.mainBackendVersion].groups;
    }
    static async getAdminGroupsRoute() {
        const routes = await this.getRoutes();
        // admin endpoints are not exposed in the gateway routes config, so derive them from the regular route
        return routes.main[RouteService.mainBackendVersion].groups.replace("/main/api/", "/main/admin/");
    }
    static async getPoliciesRoute() {
        const routes = await this.getRoutes();
        return routes.main["v3"].policies;
    }
    static async getImagesRoute() {
        const routes = await this.getRoutes();
        return routes.main[RouteService.mainBackendVersion].images;
    }
    static async getJwksRoute() {
        const routes = await this.getRoutes();
        return routes.main.jwKs;
    }
    static async getLicensesRoute() {
        const routes = await this.getRoutes();
        return routes.main[RouteService.mainBackendVersion].licenses;
    }
    static async getAdminLicensesRoute() {
        const routes = await this.getRoutes();
        // admin endpoints are not exposed in the gateway routes config, so derive them from the regular route
        return routes.main[RouteService.mainBackendVersion].licenses.replace("/main/api/", "/main/admin/");
    }
    static async getPaymentRoute() {
        const routes = await this.getRoutes();
        return routes.main[RouteService.mainBackendVersion].payments;
    }
    static async getProductRoute() {
        const routes = await this.getRoutes();
        return routes.product[RouteService.productBackendVersion];
    }
    static async getProvisioningsRoute() {
        const routes = await this.getRoutes();
        return routes.main[RouteService.mainBackendVersion].provisionings;
    }
    static async getSecurityRoute() {
        const routes = await this.getRoutes();
        return routes.main["v3"].security;
    }
    static async getSettingsRoute() {
        const routes = await this.getRoutes();
        return routes.main[RouteService.mainBackendVersion].settings;
    }
    static async getStorageRoute() {
        const routes = await this.getRoutes();
        return routes.storage[RouteService.storageBackendVersion];
    }
    static async getStreamRoute() {
        const routes = await this.getRoutes();
        return routes.stream[RouteService.streamBackendVersion];
    }
    static async getSubscriptionsRoute() {
        const routes = await this.getRoutes();
        return routes.main[RouteService.mainBackendVersion].subscriptions;
    }
    static async getTokensRoute() {
        const routes = await this.getRoutes();
        return routes.main[RouteService.mainBackendVersion].tokens;
    }
    static async getVouchersRoute() {
        const routes = await this.getRoutes();
        return routes.main[RouteService.mainBackendVersion].vouchers;
    }
    static async getWorkspacesRoute() {
        const routes = await this.getRoutes();
        return routes.main[RouteService.mainBackendVersion].workspaces;
    }
    static async getFdroidReposRoute() {
        const routes = await this.getRoutes();
        return routes.main["v3"].fdroidrepos;
    }
}

export default RouteService;
